import React, { memo, useCallback, useEffect, useRef } from "react";
import { Animated, Platform, Pressable, StyleSheet, View } from "react-native";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import type { ToastItem, ToastType } from "@/lib/toast";

// ─── Design tokens ───────────────────────────────────────────────────────────
// Mapped from tailwind.config.js tokens — no raw hex in product code.
//
// Tailwind cannot be used inside Animated.View inline styles, and the classes
// are applied via `className` on static elements only. The container background
// and border use StyleSheet objects so they work with Animated.View.

interface VariantStyle {
  /** Tailwind classes for the inner content container. */
  contentClass: string;
  /** Leading icon symbol (uses AppText for locale font). */
  icon: string;
  /** Tailwind text class for the icon colour. */
  iconColor: string;
}

const VARIANT_STYLES: Record<ToastType, VariantStyle> = {
  success: {
    contentClass: "bg-success-light border border-success-border rounded-lg",
    icon: "✓",
    iconColor: "text-success",
  },
  error: {
    contentClass: "bg-error-light border border-error-border rounded-lg",
    icon: "✕",
    iconColor: "text-error",
  },
  info: {
    contentClass: "bg-blue-5 border border-blue-10 rounded-lg",
    icon: "ℹ",
    iconColor: "text-blue-50",
  },
};

const TOAST_A11Y_KEYS = {
  success: "toast.a11y.success",
  error: "toast.a11y.error",
  info: "toast.a11y.info",
} as const;

const ANIMATION_DURATION_IN = 300;
const ANIMATION_DURATION_OUT = 250;
const SLIDE_OFFSET = -80; // px, starts above visible area

// ─── ToastItem component ─────────────────────────────────────────────────────

interface ToastItemProps {
  item: ToastItem;
  onDismiss: (id: string) => void;
  showLeftIcon?: boolean;
  showCloseIcon?: boolean;
}

/**
 * Single animated toast notification.
 *
 * Slides down from above the screen and fades in on mount.
 * Auto-dismisses after `item.duration` ms (default 4000).
 * Can be dismissed early by tapping.
 */
export const ToastItemView = memo(function ToastItemView({
  item,
  onDismiss,
  showLeftIcon = false,
  showCloseIcon = false,
}: ToastItemProps) {
  const { t } = useI18n();
  const slideAnim = useRef(new Animated.Value(SLIDE_OFFSET)).current;
  const opacityAnim = useRef(new Animated.Value(0)).current;

  const dismiss = useCallback(() => {
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: SLIDE_OFFSET,
        duration: ANIMATION_DURATION_OUT,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 0,
        duration: ANIMATION_DURATION_OUT,
        useNativeDriver: true,
      }),
    ]).start(() => onDismiss(item.id));
  }, [slideAnim, opacityAnim, onDismiss, item.id]);

  useEffect(() => {
    // Slide in & fade in
    Animated.parallel([
      Animated.timing(slideAnim, {
        toValue: 0,
        duration: ANIMATION_DURATION_IN,
        useNativeDriver: true,
      }),
      Animated.timing(opacityAnim, {
        toValue: 1,
        duration: ANIMATION_DURATION_IN,
        useNativeDriver: true,
      }),
    ]).start();

    // Auto-dismiss
    const duration = item.duration ?? 4000;
    if (duration > 0) {
      const timer = setTimeout(dismiss, duration);
      return () => clearTimeout(timer);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const { icon, iconColor, contentClass } = VARIANT_STYLES[item.type];
  const iconA11yLabel = t(TOAST_A11Y_KEYS[item.type]);

  return (
    <Animated.View
      style={[
        styles.animatedWrapper,
        { transform: [{ translateY: slideAnim }], opacity: opacityAnim },
      ]}
      className="mb-1"
      accessibilityLiveRegion="polite"
      accessibilityRole="alert"
      testID={`toast-item-alert-${item.id}`}
    >
      <Pressable
        onPress={() => {
          item.onPress?.();
          dismiss();
        }}
        accessibilityRole="button"
        accessibilityLabel={`${iconA11yLabel}: ${item.message}. Tap to dismiss.`}
        testID={`toast-item-${item.id}`}
      >
        <View className={`flex-row items-start gap-3 px-4 py-3 ${contentClass}`}>
          {/* ── Icon ──────────────────────────────── */}
          {showLeftIcon && (
            <AppText
              weight="bold"
              className={`text-lg leading-tight mt-0.5 ${iconColor}`}
              accessible={false}
            >
              {icon}
            </AppText>
          )}

          {/* ── Text ──────────────────────────────── */}
          <View className="flex-1">
            {item.title != null && (
              <AppText weight="semibold" className="text-base text-dark-text-50 mb-0.5">
                {item.title}
              </AppText>
            )}
            <AppText weight="medium" className="text-sm text-supporting-text">
              {item.message}
            </AppText>
          </View>

          {/* ── Dismiss button ────────────────────── */}
          {showCloseIcon && (
            <AppText
              weight="regular"
              className="text-dark-text-15 text-base leading-tight mt-0.5"
              accessible={false}
            >
              ✕
            </AppText>
          )}
        </View>
      </Pressable>
    </Animated.View>
  );
});

// ─── ToastContainer ──────────────────────────────────────────────────────────

interface ToastContainerProps {
  toasts: ToastItem[];
  onDismiss: (id: string) => void;
}

/**
 * Absolutely-positioned overlay container that renders all active toasts
 * stacked vertically at the top of the screen, respecting safe-area insets.
 *
 * Rendered as a child of `ToastProvider` — you should not need to render
 * this directly.
 */
export function ToastContainer({ toasts, onDismiss }: ToastContainerProps) {
  const insets = useSafeAreaInsets();

  if (toasts.length === 0) return null;

  return (
    <View
      style={[styles.container, { top: insets.top + 8 }]}
      pointerEvents="box-none"
      testID="toast-container"
      className="tablet:max-w-md w-full mx-auto px-page-x self-center z-50 absolute"
    >
      {toasts.map((item) => (
        <ToastItemView
          key={item.id}
          item={item}
          onDismiss={onDismiss}
          showLeftIcon={item.showLeftIcon}
          showCloseIcon={item.showCloseIcon}
        />
      ))}
    </View>
  );
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    // On Android, elevation is needed for the overlay to sit above screens
    ...Platform.select({ android: { elevation: 20 } }),
  },
  animatedWrapper: {
    // Shadow for iOS
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.12,
        shadowRadius: 6,
      },
    }),
  },
});
